// src/services/usersService.js
import { auth, db, serverTimestamp } from "../firebaseConfig";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth";

/**
 * Upserts users/{uid} after email OTP is verified (anonymous or signed-in).
 * Ensures displayName, email, phoneNumber, emailVerified, timestamps, shopId, deviceId.
 *
 * @param {Object} contact - expects at least { firstName?, phone?, isGuest?, shopId?, deviceId?, emailVerified? }
 * @param {string} email   - verified email (from OTP flow)
 * @returns {Promise<{ uid: string, name: string | null }>}
 */
export async function upsertUserProfile(contact = {}, email) {
  const u = auth.currentUser;
  if (!u) throw new Error("AUTH_REQUIRED");

  // We require only email verification (guest flow)
  if (contact.emailVerified !== true) {
    throw new Error("EMAIL_VERIFICATION_REQUIRED");
  }

  const uid = u.uid;
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);

  // name handling: use typed full name, else Auth displayName
  const rawName = (contact.firstName || u.displayName || "").trim();
  const displayName = rawName || null;
  const [firstName, ...rest] = (rawName || "").split(/\s+/);
  const lastName = rest.join(" ") || null;

  // persist Auth displayName if missing
  if (!u.displayName && displayName) {
    try { await updateProfile(u, { displayName }); } catch (e) { /* ignore */ }
  }

  // make sure we always persist these fields
  const data = {
    uid,
    displayName,                 // e.g., "Ali MAMMADOV"
    firstName: firstName || null,
    lastName,
    email: email || u.email || null,
    emailVerified: true,         // <-- persist as TRUE once OTP verified
    phoneNumber: contact.phone || u.phoneNumber || null,

    isGuest: contact.isGuest ?? true,
    shopId: contact.shopId || null,
    deviceId: contact.deviceId || null,

    role: "user",
    status: "active",
    providerIds: (u.providerData || []).map(p => p.providerId),

    lastLoginAt: serverTimestamp(),
    modifiedAt: serverTimestamp(),
    ...(snap.exists() ? {} : { createdAt: serverTimestamp() }),
  };

  if (snap.exists()) {
    await updateDoc(ref, data);
  } else {
    await setDoc(ref, data);
  }

  return { uid, name: displayName };
}
